import {
  parseWorkspaceNodeReference,
  type WorkspaceNodeReference,
} from '@/lib/workspace-node-reference';
import { workspaceFilesHref } from '@/convex/products/ai/navigation/workspace_entity_routes.helpers';

// One implementation shared with Convex navigation, which must build the same URLs.
export {
  WORKSPACE_FILES_NODE_REFERENCE_PARAM,
  workspaceFilesHref,
  workspaceFilesNodeReferenceHref,
} from '@/convex/products/ai/navigation/workspace_entity_routes.helpers';

export type WorkspaceFilesNodeReferenceIntent =
  | { kind: 'path' }
  | { kind: 'invalid-reference' }
  | { kind: 'node-reference'; reference: WorkspaceNodeReference };

/** Decode one optional-catch-all route segment without rejecting old URLs. */
export function decodeWorkspaceFilePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Whether a route pathname and a link href name the same route, however
 * each is percent-encoded (`usePathname()` is encoded, hrefs may encode
 * differently, e.g. `@` kept or escaped). Segments are decoded one at a time,
 * and a malformed escape (a hand-typed `/100%`) is compared as typed instead
 * of throwing during render.
 */
export function workspaceRoutePathsMatch(
  pathname: string | null | undefined,
  href: string
): boolean {
  if (!pathname) return false;
  const decodePath = (path: string) =>
    path.split('/').map(decodeWorkspaceFilePathSegment).join('/');
  return decodePath(pathname) === decodePath(href);
}

export function workspaceFilesPathFromParams(
  orgSlug: string,
  filePath: string[] | undefined
): string {
  const rawPath = (filePath ?? [])
    .map(decodeWorkspaceFilePathSegment)
    .join('/');
  const withoutLeadingSlash = rawPath.replace(/^\/+/, '');
  if (!withoutLeadingSlash) return `/${orgSlug}`;
  if (
    withoutLeadingSlash === orgSlug ||
    withoutLeadingSlash.startsWith(`${orgSlug}/`)
  ) {
    return `/${withoutLeadingSlash}`;
  }
  return `/${orgSlug}/${withoutLeadingSlash}`;
}

/**
 * The IMAI App route for an app folder: the app runs in its own window at
 * /<org>/apps/<path>, wherever it was opened from.
 */
export function workspaceAppHref(orgSlug: string, path: string): string {
  return workspaceFilesHref(orgSlug, path).replace(
    `/${orgSlug}/files/`,
    `/${orgSlug}/apps/`
  );
}

/**
 * The workspace path an /<org>/apps/<...segments> route names. The segments
 * are still percent-encoded, exactly once: the raw URL pathname on the
 * server, and `useParams()` on the client (Next canonicalizes catch-all
 * values to `encodeURIComponent` form, see next/dist/client/route-params.js
 * `canonicalizeURLPart` and get-dynamic-param.js). So each is decoded once;
 * a malformed escape (a hand-typed `%`) is kept as typed rather than throwing.
 */
export function workspaceAppPathFromSegments(
  orgSlug: string,
  segments: readonly string[]
): string {
  const decoded = segments.map(decodeWorkspaceFilePathSegment);
  return `/${[orgSlug, ...decoded].join('/')}`;
}

export function workspaceFilesNodeReferenceIntent(
  value: string | null
): WorkspaceFilesNodeReferenceIntent {
  if (value === null) return { kind: 'path' };
  try {
    const reference = parseWorkspaceNodeReference(JSON.parse(value));
    return reference
      ? { kind: 'node-reference', reference }
      : { kind: 'invalid-reference' };
  } catch {
    return { kind: 'invalid-reference' };
  }
}

export function isWorkspaceDrivesRootPath(
  orgSlug: string,
  path: string
): boolean {
  return path === `/${orgSlug}/drives`;
}

/** Keep the permissions inspector scoped to the item that opened it. */
export function workspacePermissionsPanelIsOpen(
  openPath: string | null,
  requestedPath: string
): boolean {
  return openPath === requestedPath;
}

/** Bare and pre-username `/users` entry routes both open the current home. */
export function shouldRedirectWorkspaceFilesToHome(
  filePath: string[] | undefined
): boolean {
  return (
    !filePath ||
    filePath.length === 0 ||
    (filePath.length === 1 &&
      decodeWorkspaceFilePathSegment(filePath[0] ?? '') === 'users')
  );
}

/** Select one Finder-rail location, preferring the deepest matching path. */
export function activeWorkspaceFilesRailPath(
  requestedPath: string,
  targetPaths: ReadonlyArray<string>
): string | null {
  let activePath: string | null = null;
  for (const targetPath of targetPaths) {
    if (
      requestedPath !== targetPath &&
      !requestedPath.startsWith(`${targetPath}/`)
    ) {
      continue;
    }
    if (activePath === null || targetPath.length > activePath.length) {
      activePath = targetPath;
    }
  }
  return activePath;
}

/** Select one rail target, giving favorites precedence over the Home fallback. */
export function resolveWorkspaceFilesRailTarget({
  requestedPath,
  homePath,
  favoritePaths,
  workspacePaths,
  utilityPaths = [],
}: {
  requestedPath: string;
  homePath: string | null;
  favoritePaths: ReadonlyArray<string>;
  workspacePaths: ReadonlyArray<string>;
  utilityPaths?: ReadonlyArray<string>;
}): string | null {
  const favoritePath = activeWorkspaceFilesRailPath(
    requestedPath,
    favoritePaths
  );
  if (favoritePath) return favoritePath;
  if (
    homePath &&
    activeWorkspaceFilesRailPath(requestedPath, [homePath]) === homePath
  ) {
    return homePath;
  }
  const utilityPath = activeWorkspaceFilesRailPath(requestedPath, utilityPaths);
  if (utilityPath) return utilityPath;
  return activeWorkspaceFilesRailPath(requestedPath, workspacePaths);
}

/** Folders that can be a task's own root: inside a Home or a Drive. */
export function couldBeTaskFolderPath(orgSlug: string, path: string): boolean {
  const segments = path.split('/').filter(Boolean);
  return (
    segments.length >= 4 &&
    segments[0] === orgSlug &&
    (segments[1] === 'users' || segments[1] === 'drives')
  );
}
